import { Button } from "GameObjects/UI/Button";
import * as Constants from "Data/Constants";
import * as HLF from "html-living-framework";
import * as Topics from "Data/Topics";
import * as SFX from "Data/SoundEffect";
import * as VO from "Data/VoiceOver";
import Settings from "Data/Settings";
import {DuckSwapGameLayout} from "Layouts/DuckSwapGameLayout";
import { LayoutSprite } from "GameObjects/UI/LayoutSprite";
import { Text } from "GameObjects/UI/Text";
import { AbstractGameState } from "./AbstractGameState";
import { RestStopGame } from "./RestStopGame";
import { FlyingGame } from "./FlyingGame";
import { SwapGameDuck } from "GameObjects/SwapGame/SwapGameDuck";
import { TimelineMax, TweenLite, TweenMax } from "gsap";
import GameState, { IDuckConfig, IRuntimeDuck } from "../Data/GameState";
import { IDuckState } from "../Data/GameState";
import { Background } from "GameObjects/UI/Background";
import { LayoutButton } from "GameObjects/UI/LayoutButton";
import { LayoutNineSliceSprite } from "GameObjects/UI/LayoutNineSliceSprite";
import { Application } from "Application";

export class DuckSwapGame extends AbstractGameState<typeof DuckSwapGameLayout> implements ReturnType<typeof DuckSwapGameLayout> {

    public static readonly NAME = "DuckSwapGame";
    protected static firstTime: boolean = true;
    public readonly layout = DuckSwapGameLayout;

    public readonly bg: LayoutSprite;
    public readonly templateDuck: LayoutSprite;
    public readonly energyBarSegment: LayoutSprite[];
    public readonly energyBarEmpty: LayoutSprite;
    public readonly duckNameBg: LayoutNineSliceSprite;
    public readonly doneButton = this.addChild(new LayoutButton(this.onDoneButtonClicked.bind(this), VO.DONE, SFX.UI_CLICK));

    protected ducks: SwapGameDuck[] = [];

    protected initialLeadDuck: SwapGameDuck | undefined;

    protected selectedDuck?: number; // swap from
    protected selectedDuck2?: number; // swap to

    protected swapTimeline = new TimelineMax();

    protected bgContainer = this.addChildAt(new Background(), 0);

    protected wasCorrectSwap: boolean = false;
    protected bgm = HLF.Application.instance.audio.getAudioTrack("duckswaploop_2", HLF.AudioCategory.BGM) ?? HLF.Application.instance.audio.createAudioTrack("duckswaploop_2", HLF.AudioCategory.BGM);

    protected energyMeters: PIXI.Container[];

    private idleTimeline?: TimelineMax;

    private readonly centerThisContainer: PIXI.Container;

    public constructor() {
        super();
        this.bg = this.bgContainer.addChild(new LayoutSprite());

        this.templateDuck = new LayoutSprite();
        this.energyBarSegment = [
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
        ];
        this.energyBarEmpty = new LayoutSprite();
        this.duckNameBg = new LayoutNineSliceSprite(18, 0, 18, 0);
        this.energyMeters = [];

        this.centerThisContainer = new PIXI.Container();
    }


    public init(pSize: PIXI.Point, pData: {name: string, ducks?: IRuntimeDuck[]}) {
        super.init(pSize, pData);

        this.addChild(this.templateDuck);
        this.addChild(this.energyBarEmpty);
        this.addChild(...this.energyBarSegment);
        this.addChild(this.duckNameBg);
        this.addChild(this.centerThisContainer);

        if (pData.ducks === undefined) {
            console.log("initializing ducks with new data");
            let duckCount = 5;
            if (Settings.difficulty === (2/3)) {
                duckCount = 7;
            }
            else if (Settings.difficulty === (3/3)) {
                duckCount = 9;
            }
            pData.ducks = GameState.GetDucks(duckCount);
            HLF.ArrayUtils.shuffle(pData.ducks);
        }
        else {
            console.log("got ducks data from previous scene");
        }

        this.initDucks(pData.ducks);

        this.layoutDucksInV({animate: false});
        if (this.ducks.length === 5) {
            this.centerThisContainer.position.x = -175;
        }
        else if (this.ducks.length === 7) {
            this.centerThisContainer.position.x = -75;
        }

        this.doneButton.visible = false;

        this.ducks[0].select();
    }

    public animateIn(pOnComplete: ()=>void) {
        super.animateIn(pOnComplete);

        TweenMax.staggerFrom(this.energyBarSegment, 0.1, {pixi: {alpha: 0, scale: 0}}, 0.075);

        if (Settings.closedCaptionsEnabled === false && Settings.voiceoverVolume === 0) {
            PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [
                ...this.ducks,
            ]);
        }

        if (DuckSwapGame.firstTime) {
            console.log("First time encountering duck swap -- onboarding starts now");
            Application.instance.analytics.event("tutorial_begin", DuckSwapGame.NAME);

            HLF.Application.instance.voiceover.playVO(VO.DUCK_SWAP_ONBOARDING_1A);
            HLF.Application.instance.voiceover.playVO([
                this.ducks[0].config.nameVO_swap,
                VO.DUCK_SWAP_ONBOARDING_1B,
            ], {
                mode: HLF.PlayMode.Append,
                caption: {
                    id: VO.DUCK_SWAP_ONBOARDING_1B,
                    args: {
                        "DUCK": this.ducks[0].config.nameVO_swap,
                    },
                },
                callback: ()=>{
                    if (this._destroyed) return;
                    if (Settings.audioDescriptionEnabled === false) {
                        const pulseEnergyMeters = new TimelineMax({repeat: -1, repeatDelay: 0.5});
                        pulseEnergyMeters.to(this.energyMeters, 0.25, {pixi: {scale: 1.25}})
                            .to(this.energyMeters, 0.5, {pixi: {scale: 1}});
                        HLF.Application.instance.voiceover.playVO([VO.DUCK_SWAP_ONBOARDING_2], HLF.PlayMode.Append, ()=>{
                            if (this._destroyed) return;
                            HLF.Application.instance.voiceover.playVO([VO.DUCK_SWAP_ONBOARDING_3], ()=>{
                                pulseEnergyMeters.kill();
                                if (this._destroyed) return;
                                HLF.Application.instance.voiceover.playVO([VO.DUCK_SWAP_IDLE], ()=>{
                                    this.restartIdleTimer();
                                });
                                this.ducks.forEach((duck, i) => {
                                    if (i > 0) {
                                        duck.pulse();
                                    }
                                });
                            });
                            PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [
                                ...this.ducks,
                            ]);
                        });
                    }
            }});
        }
        else {
            HLF.Application.instance.voiceover.playVO([this.ducks[0].config.nameVO_swap, VO.DUCK_SWAP_RETURN_VISIT], {
                caption: {
                    id: VO.DUCK_SWAP_RETURN_VISIT,
                    args: {"DUCK": this.ducks[0].config.nameVO_swap},
                },
                callback:()=>{
                    this.restartIdleTimer();
                    PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [
                        ...this.ducks,
                    ]);
                }});
        }

        if (Settings.audioDescriptionEnabled) {
            const vosToPlay: string[] = [
                VO.SWAP_AUDIO_DESCRIPTION_1A,
                VO.NUMBER_ZERO,
                VO.SWAP_AUDIO_DESCRIPTION_1B,
            ];

            let numberVO: string;
            switch (Settings.difficulty) {
                case 0 / 3: // adaptive difficulty
                    switch (this.ducks.length) {
                        default:
                        case 5:
                            numberVO = VO.NUMBER_FIVE;
                            break;
                        case 7:
                            numberVO = VO.NUMBER_TWENTYFIVE;
                            break;
                        case 9:
                            numberVO = VO.NUMBER_FIFTY;
                            break;
                    }
                break;
                case 1 / 3:
                    numberVO = VO.NUMBER_FIVE;
                    break;
                case 2 / 3:
                    numberVO = VO.NUMBER_TWENTYFIVE;
                    break;
                case 3 / 3:
                    numberVO = VO.NUMBER_FIFTY;
                    break;
            }
            vosToPlay.push(numberVO!);

            Application.instance.voiceover.playVO(vosToPlay, {
                mode: HLF.PlayMode.Append,
                caption: {
                    id: VO.SWAP_AUDIO_DESCRIPTION_1A,
                    args: {
                        "ZERO": VO.NUMBER_ZERO,
                        "NUMBER": numberVO!,
                        "_": VO.SWAP_AUDIO_DESCRIPTION_1B, // hack to extend duration of caption
                    }
                },
                callback: ()=>{
                    if (this._destroyed) return;
                    const vosToPlay2 = [
                        VO.SWAP_AUDIO_DESCRIPTION_2A,
                        this.ducks[0].config.nameVO_swap,
                        VO.SWAP_AUDIO_DESCRIPTION_2B
                    ];
                    let numberVO2: string;
                    const duckEnergyOutOfFive = getEnergyOutOfFive(this.ducks[0]);

                    switch (Settings.difficulty) {
                        case 0 / 3: // adaptive difficulty
                            switch (this.ducks.length) {
                                default:
                                case 5:
                                    switch (duckEnergyOutOfFive) {
                                        case 0:
                                            numberVO2 = VO.NUMBER_ZERO;
                                            break;
                                        case 1:
                                            numberVO2 = VO.NUMBER_ONE;
                                            break;
                                        case 2:
                                            numberVO2 = VO.NUMBER_TWO;
                                            break;
                                        case 3:
                                            numberVO2 = VO.NUMBER_THREE;
                                            break;
                                        case 4:
                                            numberVO2 = VO.NUMBER_FOUR;
                                            break;
                                        case 5:
                                            numberVO2 = VO.NUMBER_FIVE;
                                            break;
                                    }
                                    break;
                                case 7:
                                    switch (duckEnergyOutOfFive) {
                                        case 0:
                                            numberVO2 = VO.NUMBER_ZERO;
                                            break;
                                        case 1:
                                            numberVO2 = VO.NUMBER_FIVE;
                                            break;
                                        case 2:
                                            numberVO2 = VO.NUMBER_TEN;
                                            break;
                                        case 3:
                                            numberVO2 = VO.NUMBER_FIFTEEN;
                                            break;
                                        case 4:
                                            numberVO2 = VO.NUMBER_TWENTY;
                                            break;
                                        case 5:
                                            numberVO2 = VO.NUMBER_TWENTYFIVE;
                                            break;
                                    }
                                    break;
                                case 9:
                                    switch (duckEnergyOutOfFive) {
                                        case 0:
                                            numberVO2 = VO.NUMBER_ZERO;
                                            break;
                                        case 1:
                                            numberVO2 = VO.NUMBER_TEN;
                                            break;
                                        case 2:
                                            numberVO2 = VO.NUMBER_TWENTY;
                                            break;
                                        case 3:
                                            numberVO2 = VO.NUMBER_THIRTY;
                                            break;
                                        case 4:
                                            numberVO2 = VO.NUMBER_FORTY;
                                            break;
                                        case 5:
                                            numberVO2 = VO.NUMBER_FIFTY;
                                            break;
                                    }
                                    break;
                            }
                        break;
                        case 1 / 3:
                            switch (duckEnergyOutOfFive) {
                                case 0:
                                    numberVO2 = VO.NUMBER_ZERO;
                                    break;
                                case 1:
                                    numberVO2 = VO.NUMBER_ONE;
                                    break;
                                case 2:
                                    numberVO2 = VO.NUMBER_TWO;
                                    break;
                                case 3:
                                    numberVO2 = VO.NUMBER_THREE;
                                    break;
                                case 4:
                                    numberVO2 = VO.NUMBER_FOUR;
                                    break;
                                case 5:
                                    numberVO2 = VO.NUMBER_FIVE;
                                    break;
                            }
                            break;
                        case 2 / 3:
                            switch (duckEnergyOutOfFive) {
                                case 0:
                                    numberVO2 = VO.NUMBER_ZERO;
                                    break;
                                case 1:
                                    numberVO2 = VO.NUMBER_FIVE;
                                    break;
                                case 2:
                                    numberVO2 = VO.NUMBER_TEN;
                                    break;
                                case 3:
                                    numberVO2 = VO.NUMBER_FIFTEEN;
                                    break;
                                case 4:
                                    numberVO2 = VO.NUMBER_TWENTY;
                                    break;
                                case 5:
                                    numberVO2 = VO.NUMBER_TWENTYFIVE;
                                    break;
                            }
                            break;
                        case 3 / 3:
                            switch (duckEnergyOutOfFive) {
                                case 0:
                                    numberVO2 = VO.NUMBER_ZERO;
                                    break;
                                case 1:
                                    numberVO2 = VO.NUMBER_TEN;
                                    break;
                                case 2:
                                    numberVO2 = VO.NUMBER_TWENTY;
                                    break;
                                case 3:
                                    numberVO2 = VO.NUMBER_THIRTY;
                                    break;
                                case 4:
                                    numberVO2 = VO.NUMBER_FORTY;
                                    break;
                                case 5:
                                    numberVO2 = VO.NUMBER_FIFTY;
                                    break;
                            }
                            break;
                    }
                    vosToPlay2.push(numberVO2!);

                    Application.instance.voiceover.playVO(vosToPlay2, {
                        mode: HLF.PlayMode.Append,
                        caption: {
                            id: VO.SWAP_AUDIO_DESCRIPTION_2A,
                            args: {
                                "DUCK": this.ducks[0].config.nameVO_swap,
                                "NUMBER": numberVO2!,
                                "_": VO.SWAP_AUDIO_DESCRIPTION_2B, // hack to extend duration of caption
                            }
                        },
                        callback: ()=>{
                            if (this._destroyed) return;
                            Application.instance.voiceover.playVO([
                                VO.SWAP_AUDIO_DESCRIPTION_3_ALT,
                                VO.SWAP_AUDIO_DESCRIPTION_4,
                            ], ()=>{
                                this.restartIdleTimer();
                            });
                            PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [
                                ...this.ducks,
                            ]);
                        }
                    });
                }});
        }

        PubSub.publishSync(HLF.CLEAR_FOCUS);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
    }

    public gotoNextState(name: string, data?: object) {
        this.stopIdleTimer();
        super.gotoNextState(name, {ducks: this.ducks, from: DuckSwapGame.NAME, wasCorrectSwap: this.wasCorrectSwap, ...data});
    }

    public destroy(pOptions: Parameters<typeof HLF.State.prototype.destroy>[0]) {
        this.idleTimeline?.kill();
        super.destroy(pOptions);
    }

    protected onDuckSelected(pDuckIndex: number) {
        this.ducks.forEach(duck => duck.stopPulse());

        if (this.selectedDuck === undefined) {
            this.selectedDuck = pDuckIndex;
            // one duck selected
        }
        else if (this.selectedDuck2 === undefined) {
            // two ducks selected
            this.selectedDuck2 = pDuckIndex;
            this.swapDucks(this.selectedDuck, this.selectedDuck2);
        }
        else {
            // NOTE: this should never happen
        }
    }

    protected onDuckDeselected(pDuckIndex: number) {
        if (this.selectedDuck !== undefined) {
            if (this.selectedDuck === pDuckIndex) {
                this.selectedDuck = undefined;
                if (this.selectedDuck2 !== undefined) {
                    this.selectedDuck = this.selectedDuck2;
                    this.selectedDuck2 = undefined; // duck 2 is now duck 1
                    // one duck selected
                }
                else {
                    // zero ducks selected -- this should no longer be possible
                }
            }
        }

        if (this.selectedDuck2 !== undefined) {
            if (this.selectedDuck2 === pDuckIndex) {

                this.selectedDuck2 = undefined;

                if (this.selectedDuck === undefined) {
                    this.selectedDuck = pDuckIndex; // duck 2 is now duck 1
                    // one duck selected
                }
                else {
                    // zero ducks selected -- this should no longer be possible
                }
            }
        }
    }

    protected swapDucks(pDuck1: number, pDuck2: number) {
        this.stopIdleTimer();

        [this.ducks[pDuck1], this.ducks[pDuck2]] = [this.ducks[pDuck2], this.ducks[pDuck1]]; // https://flaviocopes.com/javascript-swap-array-elements/

        this.ducks[pDuck2].deselect();

        PubSub.publishSync(HLF.CLEAR_FOCUS); // hide keyboard focus

        this.layoutDucksInV({animate: true});

        const wasInitialLeadDuck = this.ducks[0] === this.initialLeadDuck;
        this.doneButton.visible = !wasInitialLeadDuck;

        this.swapTimeline.call(()=>{
            let leadDuckEnergyOutOfFive: number | undefined;
            let previousLeadDuckEnergyOutOfFive;

            if (pDuck1 === 0) {
                leadDuckEnergyOutOfFive = getEnergyOutOfFive(this.ducks[pDuck1]);
                previousLeadDuckEnergyOutOfFive = getEnergyOutOfFive(this.ducks[pDuck2]);
                Application.instance.analytics.event("select_content", this.ducks[pDuck1].name);
            }
            else if (pDuck2 === 0) {
                leadDuckEnergyOutOfFive = getEnergyOutOfFive(this.ducks[pDuck2]);
                previousLeadDuckEnergyOutOfFive = getEnergyOutOfFive(this.ducks[pDuck1]);
                Application.instance.analytics.event("select_content", this.ducks[pDuck2].name);
            }

            if (wasInitialLeadDuck) {
                this.wasCorrectSwap = false;
                GameState.swaptastic = false;
                HLF.Application.instance.audio.play(SFX.BAD_PICK, 1, false, HLF.AudioCategory.SFX);
                HLF.Application.instance.voiceover.playVO(VO.DUCK_SWAP_OLD_LEAD, {priority: 3, callback: ()=>{
                    this.restartIdleTimer();
                }});
            }
            else if (leadDuckEnergyOutOfFive !== undefined && previousLeadDuckEnergyOutOfFive !== undefined) {
                if (leadDuckEnergyOutOfFive > 1) {
                    this.wasCorrectSwap = true;
                    HLF.Application.instance.audio.play(SFX.GOOD_PICK, 1, false, HLF.AudioCategory.SFX);
                    if (leadDuckEnergyOutOfFive > 2) {
                        HLF.Application.instance.voiceover.playVO(VO.DUCK_SWAP_CORRECT_ANSWER, {priority: 3, callback: ()=>{
                            this.restartIdleTimer();
                        }});
                    }
                    else {
                        HLF.Application.instance.voiceover.playVO(VO.DUCK_SWAP_CORRECT_ANSWER_TWO_ENERGY, {priority: 3, callback: ()=>{
                            this.restartIdleTimer();
                        }});
                    }
                    const allDucksEnergiesOutOfFive: number[] = this.ducks.map(d=>getEnergyOutOfFive(d));
                    const comparator = leadDuckEnergyOutOfFive;
                    const wasThereABetterSwap: boolean = allDucksEnergiesOutOfFive.some(e => e > comparator);
                    if (wasThereABetterSwap) {
                        GameState.swaptastic = false;
                    }
                    console.log({allDucksEnergiesRounded: allDucksEnergiesOutOfFive, newLeadDuckEnergyRounded: leadDuckEnergyOutOfFive, wasThereABetterSwap});
                }
                else {
                    this.wasCorrectSwap = false;
                    HLF.Application.instance.audio.play(SFX.BAD_PICK, 1, false, HLF.AudioCategory.SFX);
                    HLF.Application.instance.voiceover.playVO(VO.DUCK_SWAP_WRONG_ANSWER, {priority: 3, callback: ()=>{
                        this.restartIdleTimer();
                    }});
                    GameState.swaptastic = false;
                }
            }

            // remove and re-add ducks to keyboard map because order changed
            PubSub.publishSync(HLF.UNREGISTER_FOCUSABLES, [
                ...this.ducks,
                this.doneButton,
                this.homeButton,
            ]);
            if (!wasInitialLeadDuck) {
                PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [
                    ...this.ducks,
                    this.doneButton,
                    this.homeButton,
                ]);
                PubSub.publishSync(HLF.FORCE_FOCUS, this.doneButton);
                HLF.Application.instance.voiceover.playVO(VO.DUCK_SWAP_PROMPT_DONE, {priority: 3, mode: HLF.PlayMode.Append, callback:()=>{
                    this.restartIdleTimer();
                }});
            }
            else {
                PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [
                    ...this.ducks,
                    this.homeButton,
                ]);
            }
        });

        if (!wasInitialLeadDuck) {
            if (DuckSwapGame.firstTime) {
                this.doneButton.pulse();
            }
            this.ducks[pDuck1].deselect();
        }
    }

    protected onDoneButtonClicked() {
        if (DuckSwapGame.firstTime) {
            this.doneButton.stopPulse();
            DuckSwapGame.firstTime = false;
            console.log("duck swap onboarding complete");
            Application.instance.analytics.event("tutorial_complete", DuckSwapGame.NAME);
        }
        HLF.Application.instance.voiceover.stopVO();
        this.gotoNextState(FlyingGame.NAME);
    }

    protected playHelpVO() {
        HLF.Application.instance.voiceover.playVO([
            this.ducks[0].config.nameVO_swap,
            VO.DUCK_SWAP_ONBOARDING_1B,
        ],{caption: {
            id: VO.DUCK_SWAP_ONBOARDING_1B,
            args: {
                "DUCK": this.ducks[0].config.nameVO_swap,
            },
        }, callback: ()=>{
            HLF.Application.instance.voiceover.playVO([VO.DUCK_SWAP_ONBOARDING_3]);
        }});
    }

    protected onGameUnPaused() {
        super.onGameUnPaused();
        // edge case: make sure ducks are selectable with keyboard if user changed windows during transition
        PubSub.publishSync(HLF.UNREGISTER_FOCUSABLES, [
            ...this.ducks,
        ]);
        PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [
            ...this.ducks,
        ]);
    }

    private layoutDucksInV (pOptions: {animate: boolean}) {
        const duckXSpacing = 100;  // eyeballed to match comp
        const secondDuckYPositionUpperRow = 285; // measured from comp
        const secondDuckYPositionLowerRow = 450; // measured from comp
        const leadDuckYPosition = 375; // measured from comp
        const duckYSpacing = 5;
        const leadDuckX = 910; // eyeballed to match comp
        const swapSpeed = 0.5;
        const delayBetweenDucks = 0.015;

        this.swapTimeline.clear();

        for (let i = 0; i < this.ducks.length; i++) {
            const duck = this.ducks[i];
            if (i === 0) { // lead duck
                const x = leadDuckX;
                const y = leadDuckYPosition;

                if (pOptions.animate === true) {
                    this.swapTimeline.to(duck, swapSpeed, {pixi: {x, y}}, delayBetweenDucks * i);
                }
                else {
                    duck.x = x;
                    duck.y = y;
                }

            }
            else if ((i + 1) % 2 === 0) { // top ducks (left side of V)
                const x = leadDuckX - ((i + 1) * duckXSpacing);
                const y = secondDuckYPositionUpperRow - ((i + 1) * duckYSpacing);

                if (pOptions.animate === true) {
                    this.swapTimeline.to(duck, swapSpeed, {pixi: {x, y}}, delayBetweenDucks * i);
                }
                else {
                    duck.x = x;
                    duck.y = y;
                }
            }
            else if ((i + 1) % 2 === 1) { // bottom ducks (right side of v)
                const x = leadDuckX - (i * duckXSpacing);
                const y = secondDuckYPositionLowerRow + (i * duckYSpacing);

                if (pOptions.animate === true) {
                    this.swapTimeline.to(duck, swapSpeed, {pixi: {x, y}}, delayBetweenDucks * i);
                }
                else {
                    duck.x = x;
                    duck.y = y;
                }
            }
        }
    }

    private initDucks(pDucks: IRuntimeDuck[]) {

        // copy asset and preserve relative position to duck
        const cloneSprite = (sprite: PIXI.Sprite) => {
            const clone = new PIXI.Sprite(sprite.texture);
            clone.anchor.copyFrom(sprite.anchor);
            clone.position = HLF.PointUtils.subtract(sprite.position, this.templateDuck.position);
            return clone;
        }

        // copy asset and preserve relative position to duck
        const cloneNineSliceSprite = (sprite: LayoutNineSliceSprite) => {
            const clone = new LayoutNineSliceSprite(sprite.leftWidth, sprite.topHeight, sprite.rightWidth, sprite.bottomHeight);
            clone.setLayoutTexture(sprite.name, sprite.anchorX, sprite.anchorY);
            clone.position = HLF.PointUtils.subtract(sprite.position, this.templateDuck.position);
            return clone;
        }

        for (let i = 0; i < pDucks.length; i++) {
            const duck = new SwapGameDuck(pDucks[i].config, pDucks[i]);
            duck.onSelected.push(() => this.onDuckSelected(this.ducks.indexOf(duck)));
            duck.onDeselected.push(() => this.onDuckDeselected(this.ducks.indexOf(duck)));
            this.ducks.push(this.centerThisContainer.addChild(duck));

            const duckNameBg = cloneNineSliceSprite(this.duckNameBg);
            const duckNameText = new Text(duck.name, Constants.FONT_RIFFICFREE_BOLD_36, 24, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontScale: true});
            duckNameText.align = "center";
            const duckNameBgScale = Math.max((duckNameText.textWidth + 18) / duckNameBg.width, 1);
            duckNameBg.setNineSliceScale(duckNameBgScale, 1);
            duckNameBg.addChild(duckNameText);
            duck.visuals.addChild(duckNameBg); // add to .visuals so it scales on hover
            duckNameBg.scale.set(Settings.textSizeMultiplier); // scale both text and bg
            this._subscriptions.push(
                PubSub.subscribe(Topics.SETTINGS_CHANGED, ()=>{
                    duckNameBg.scale.set(Settings.textSizeMultiplier); // scale both text and bg
                }),
            );
            duckNameBg.position.x -= (duckNameBg.width / 2) * (Settings.textSizeMultiplier - 1);

            const energyMeter = this.addChild(new PIXI.Container());
            this.energyMeters.push(energyMeter);

            const energyBarBg = energyMeter.addChild(cloneSprite(this.energyBarEmpty));

            const duckEnergyOutOfFive = getEnergyOutOfFive(duck);

            for (let n = 0; n < duckEnergyOutOfFive; n++) {
                const segment = energyMeter.addChild(cloneSprite(this.energyBarSegment[n]));
                let text: Text;
                const fontSize = Settings.difficulty < 2/3 ? 24 : 22;
                switch (Settings.difficulty) {
                    default:
                    case 0 / 3: // adaptive difficulty
                        switch (pDucks.length) {
                            default:
                            case 5:
                                text = segment.addChild(new Text((n + 1).toFixed(0), Constants.FONT_RIFFICFREE_BOLD_36, fontSize, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontChangeColour: true}));
                                break;
                            case 7:
                                text = segment.addChild(new Text(((n + 1) * 5).toFixed(0), Constants.FONT_RIFFICFREE_BOLD_36, fontSize, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontChangeColour: true}));
                                break;
                            case 9:
                                text = segment.addChild(new Text(((n + 1) * 10).toFixed(0), Constants.FONT_RIFFICFREE_BOLD_36, fontSize, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontChangeColour: true}));
                                break;
                        }
                        break;
                    case 1 / 3:
                        text = segment.addChild(new Text((n + 1).toFixed(0), Constants.FONT_RIFFICFREE_BOLD_36, fontSize, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontChangeColour: true}));
                        break;
                    case 2 / 3:
                        text = segment.addChild(new Text(((n + 1) * 5).toFixed(0), Constants.FONT_RIFFICFREE_BOLD_36, fontSize, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontChangeColour: true}));
                        break;
                    case 3 / 3:
                        text = segment.addChild(new Text(((n + 1) * 10).toFixed(0), Constants.FONT_RIFFICFREE_BOLD_36, fontSize, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontChangeColour: true}));
                        break;
                }

                text.position.y = 1;

                this._subscriptions.push(
                    PubSub.subscribe(Topics.SETTINGS_CHANGED, ()=>{
                        switch (Settings.difficulty) {
                            case 0 / 3: // adaptive difficulty
                                switch (pDucks.length) {
                                    default:
                                    case 5:
                                        text.setText((n + 1).toFixed(0), 24);
                                        break;
                                    case 7:
                                        text.setText(((n + 1) * 5).toFixed(0), 22);
                                        break;
                                    case 9:
                                        text.setText(((n + 1) * 10).toFixed(0), 22);
                                        break;
                                }
                                break;
                            case 1 / 3:
                                text.setText((n + 1).toFixed(0), 24);
                                break;
                            case 2 / 3:
                                text.setText(((n + 1) * 5).toFixed(0), 22);
                                break;
                            case 3 / 3:
                                text.setText(((n + 1) * 10).toFixed(0), 22);
                                break;
                        }
                    }),
                );
                TweenLite.from(segment, 0.25, {pixi: {scale: 0}}).delay((n * 0.1)); // TODO: should really call this in animateIn
            }
            energyMeter.scale.set(HLF.MathUtils.lerp(0.75, 1, Settings.textSizeMultiplier / 1.75)); // max text size multiplier is 1.75x
                this._subscriptions.push(PubSub.subscribe(Topics.SETTINGS_CHANGED, ()=>{
                energyMeter.scale.set(HLF.MathUtils.lerp(0.75, 1, Settings.textSizeMultiplier / 1.75)); // max text size multiplier is 1.75x
            }));
            duck.addChild(energyMeter);
        }

        // hide template assets
        this.duckNameBg.visible = false;
        this.energyBarEmpty.visible = false;
        this.energyBarSegment.forEach(s=>s.visible = false);
        this.templateDuck.visible = false;

        this.initialLeadDuck = this.ducks[0];
    }


    private restartIdleTimer() {
        if (this._destroyed) return;

        if (this.idleTimeline === undefined) {
            console.log("duck swap: start idle timer");
            this.idleTimeline = new TimelineMax({repeat: -1});
            this.idleTimeline.call(()=>{
                if (this._destroyed) return;
                console.log("duck swap: play idle vo");
                // todo: random exhaust instead of random
                HLF.Application.instance.voiceover.playVO(HLF.ArrayUtils.random([
                    VO.DUCK_SWAP_IDLE,
                    VO.DUCK_SWAP_IDLE2,
                    VO.DUCK_SWAP_IDLE3,
                    VO.DUCK_SWAP_IDLE4,
                ]), ()=>this.restartIdleTimer());
            }, undefined, undefined, Settings.audioDescriptionEnabled ? 60 : 15);
        }
        else {
            this.idleTimeline?.kill();
            this.idleTimeline?.restart();
            console.log("duck swap: restart idle timer");
        }
    }

    private stopIdleTimer() {
        if (this.idleTimeline !== undefined) {
            console.log("duck swap: stop idle timer");
            this.idleTimeline.kill();
        }
    }

}
function getEnergyOutOfFive(duck: SwapGameDuck) {
    // return a value from 1 to 4, based on an input from 0 to 100
    return Math.floor(duck.energy / 20) || Math.round(duck.energy / 20) || 1;
}

